/// <amd-module name="VSS/Platform/Performance" />
import { IObservable } from "VSS/Core/Observable";
import { newGuid } from "VSS/Core/Util/String";
import { getCookie } from "VSS/Platform/Util/Cookie";
import { ICompletedContent, IContentAttributes, IVssContentService } from "./Content";
import {
    FastPageSwitchEvent,
    FastPageSwitchEventLoadingDetail,
    IVssPageContext,
    IVssService,
    ServiceOptions,
    Services,
    VssObservableService
} from "./Context";
import { Level } from "./Contracts/ClientTrace";
import { IVssContributionEventService } from "./Contribution";
import { IPostRequestEvent, RequestListenerAction, requestListeners } from "./Fetch";
import { IVssPageService } from "./Page";
import { publishEvent } from "./Telemetry";
import { getCurrentTheme } from "./Theming";
import { readJsonScript } from "./Util/Serialization";

declare global {
    interface Window {
        resourceLoadStrategy: string;
    }
}

/** Map native PerformanceEntry objects for serialization */
export interface IMappedPerformanceEntry {
    name: string;
    startTime: number;
    duration: number;
}

export interface INavigationEvent {
    name: string;
    startEvent: string;
    endEvent: string;
    perfEntry?: IMappedPerformanceEntry;
    isAggregate?: boolean;
}

export interface IServerTiming {
    elapsedTicks: number;
    startOffset: number;
    properties?: { [key: string]: any };
}

export interface IServerStatistic {
    count: number;
    elaspsedTicks: number;
    timings: IServerTiming[];
}

interface IServerStatisticsData {
    serverTimings: IServerStatistics;
    insights?: IPerformanceInsight[];
}

export type IServerStatistics = { [statisticName: string]: IServerStatistic };

/**
 * Insights about performance
 */
export interface IPerformanceInsight {
    /**
     * Name of the group to add this message to. The group name appears in the perf bar with a count next to it.
     */
    groupName: string;

    /**
     * Main message to display
     */
    title: string;

    /**
     * Optional description to add after the message
     */
    description?: string;

    /**
     * Message level
     */
    level: Level;
}

export interface IVssContentPerformanceData {
    ajaxCount: number;
    ajaxSize: number;
    cssCount: number;
    cssSize: number;
    scriptsCount: number;
    scriptSize: number;
    other: string;
}

export interface IVssServerPerformanceData {
    sql: number;
    rest: number;
    storage: number;
    redis: number;
    totalRemote: number;
}

/**
 * The IVssPerformanceData rolls up the stats about the download of content and
 * the execution of named scenarios.
 */
export interface IVssPerformanceData {
    /**
     * The amount of time taken to get to the interactive state from the start page loading.
     */
    timeToInteractive: number;

    /**
     * Value indicating whether the scenario was a background scenario
     */
    backgroundTab: boolean;

    /**
     * The contentPerformanceData is the roll up of content that has been downloaded
     * to the page. This includes things like css and javascript.
     */
    contentData: IVssContentPerformanceData;

    /**
     * The scenarioPerformanceData is the roll up of the scenarios.
     */
    serverPerformanceData: IVssServerPerformanceData;

    /**
     * Indicates if this is data for the initial load of the page.  This will be false when
     * fast page switching.
     */
    initialPageLoad: boolean;
}

/**
 * Describes split timing within scenarios
 */
export interface ISplitTiming {
    /**
     * Name of split timing
     */
    name: string;

    /**
     * Time relative to scenario start
     */
    timestamp: number;
}

/**
 * An IVssScenario is used to track a series of actions from their start to their
 * finish. When the scenario is finished it encapsulates a scenario name, the
 * timing of the scenario and any properties used to descrine scenario details.
 */
export interface IVssScenario {
    /**
     * The elapsedTicks is the number of milliseconds taken during the scenario.
     * The value is -1 until the scenario is complete.
     */
    readonly elaspsedTicks: number;

    /**
     * The time from navigationStart the scenario was completed. A started
     * scenario that hasnt ended has an endTime of -1.
     */
    readonly endTime: number;

    /**
     * Feature area that the scenario belongs to (for telemetry/reporting).
     */
    featureArea: string;

    /**
     * Determines whether or not the scenario is the primary indicator
     * of Time-to-Interactive on the page
     */
    isPageInteractive: boolean;

    /**
     * True if this scenario is recording from the last page navigation.
     * False for scenarios called with an explicit start time (like now)
     */
    isPageNavigate: boolean;

    /**
     * The properties are an extensible set of data that can be tracked with the
     * scenario and will be reported with it through telemetry.
     */
    readonly properties: { [key: string]: any };

    /**
     * The scenario name is a unique string that defines the scenario.
     */
    readonly scenarioName: string;

    /**
     * Session id that wilal be associated with this scenario's telemetry.
     */
    readonly sessionId: string;

    /**
     * Timings which happen within a scenario.
     */
    readonly splitTimings: ISplitTiming[];

    /**
     * The time from navigationStart the scenario was started. An unassigned value
     * implies the scenario started implicitly with the start of the page.
     */
    readonly startTime: number;

    /**
     * Adds-in properties which are sent in telemetry
     *
     * @param properties Set of properties to merge in.
     */
    addProperties(properties: { [key: string]: any }): void;

    /**
     * Completes this scenario, sending telemetry
     */
    complete(endTime?: number): void;
}

interface IScenarioTelemetryData {
    /**
     * Current activity id. May be client-generated.
     */
    activityId: string;

    /**
     * Indicates whether the document was hidden when the scenario ended
     */
    backgroundTab: boolean;

    /**
     * Indicates if debugMode is enabled for the application. This can be used to filter out measurements that were publihsed when debug mode is enabled
     */
    debugMode: boolean;

    /**
     * Duration of the scenario.
     */
    elapsedTime: number;

    /**
     * Name of feature area
     */
    featureArea: string;

    /**
     *  Scenario name
     */
    name: string;

    /**
     * Original activity ID from the most recent navigation (full or FPS)
     */
    pageId: string;

    /**
     * Indicates that the scenario is a web scenario that is measuring time since page-navigate-start.
     */
    pageNavigateScenario: boolean;

    /**
     * The milestone and patch version of the tfs service.
     */
    serviceVersion: string;

    /**
     * Serialized list of split timings, containing name and timestamp (relative to scenario start time) *
     */

    splitTimings: ISplitTiming[];

    /**
     * Start time of scenario, relative to browser's navigationStart event
     */
    startTime: number;

    /**
     * Indicates the timezone offset from UTC (in minutes) for the user. This can be used to do approximate geo analysis on perf data
     */
    timeZoneOffset: number;
}

export interface TelemetryEventData {
    /**
     * Area to associate with telemetry data.
     */
    area: string;

    /**
     * Feature within an area to associate with the data.
     */
    feature: string;

    /**
     * Properties to store with the telemetry
     */
    properties: { [key: string]: any };

    /**
     * Duration for the telemetry event in ms.
     */
    elapsedTime?: number;

    /**
     * The id of the service instance type to send the telemetry to
     */
    serviceInstanceType?: string;
}

interface IndividualResourceTelemetry extends IContentAttributes {
    /**
     * content-type (usually text/css or text/javascript)
     */
    contentType: string;

    /**
     * The id of the contribution that contributed this content
     */
    contributionId: string;

    /**
     * Size of the uncompressed resource body, not counting headers
     */
    decodedBodySize?: number;

    /**
     * Domain the resource is being served from
     */
    domain?: string;

    /**
     * Size of the compressed resource body, not counting headers
     */
    encodedBodySize?: number;

    /**
     * Timestamp that the request was made to load
     */
    loadStart?: number;

    /**
     * Timestamp we fire the contentComplete event
     */
    loadEnd?: number;

    /**
     * Timestamp when the resource that loaded previous to this one finished loading.  It's important to keep track of this
     * value because the total load time of a resource is roughly equal to loadEnd - max(responseEnd, previousResourceLoadEnd)
     */
    previousResourceLoadEnd?: number;

    /**
     * Timestamp indicating the beginning of the request (after DNS resolution, just before resource is requested from server)
     */
    requestStart?: number;

    /**
     * Timestamp indicating the time the client received the end of the response, or the connection was closed, whichever is first.
     */
    responseEnd?: number;

    /**
     * Timestamp we receive the first byte from the server
     */
    responseStart?: number;

    /**
     * Heuristicly determined guess of the source of this resource
     */
    source?: ResourceLoadSource;

    /**
     * Total duration from the initial fetch to the completed response
     */
    totalDuration?: number;

    /**
     * Size of the compressed full resource, including headers
     */
    transferSize?: number;
}

interface AggregateResourceTelemetry {
    /**
     * Number of resources that were loaded from the CDN. This includes all resources
     * With a cdn url, including those that were actually loaded from a cache.
     */
    cdnResourceCount: number;

    /**
     * Number of requests that were loaded from diskcache, determined heuristically.
     */
    diskcachedResourceCount: number;

    /**
     * Number of requests that were loaded from memcache, determined heuristically.
     */
    memcachedResourceCount: number;

    /**
     * Number of resources loaded before TTI
     */
    resourceCount: number;

    /**
     * A performance timestamp for the time at which we started processing blocking resource loads
     */
    resourceStartTime?: number;

    /**
     * A performance timestamp for the time at which we stopped processing blocking resource loads
     */
    resourceEndTime?: number;

    /**
     * The number of resources that were loaded before TTI, but did not contribute
     * to these aggregate timings. If this number is not 0, they should be investigated.
     */
    skippedResources: number;

    /**
     * Sum of the loading duration of all resources loaded before TTI
     */
    totalDuration: number;

    /**
     * Total number of bytes transferred across the wire for all resources
     * loaded before TTI. Includes resources loaded from cahces.
     */
    totalTransferSize: number;
}

interface ContentTelementryProperties {
    /**
     * Aggregated timing and other telemetry data for all resources loaded before TTI
     */
    aggregate: AggregateResourceTelemetry;

    /**
     * Dictionary of telemetry data for individual resources
     */
    individualTimings?: { [clientId: string]: IndividualResourceTelemetry };

    /**
     * Contains the reason for collecting such data
     */
    reason: ResourceTimingCollectionReason;
}

/**
 * The performance service is used to measure and report the performance of important
 * blocks of code and resource usage. The performance service will track each file
 * downloaded and basic cost of processing it. The performance service will track named
 * scenarios which allows callers to determine how long sensitive pieces of code take
 * for the client.
 *
 * At any given time the performance service will have the aggregated data from all
 * completed measurements and they may be retrieved via the getPerformanceData API.
 *
 * Subscribable events:
 *  action: scenarioComplete --> IVssScenario
 */
export interface IVssPerformanceService extends IVssService, IObservable<IVssScenario, "scenarioComplete"> {
    /**
     * getPerformanceData is used to return the current status of the performance
     * measurements that have been taken during the page.
     */
    getPerformanceData(): IVssPerformanceData;

    /**
     * endScenario is used to mark the completion of a scenario. If a scenario was
     * not explicitly started a new scenario empty is created and is marked as
     * starting at page navigation.
     *
     * @param featureArea - Area to associate with scenario.  Way to group scenarios which are related.
     *
     * @param scenarioName - Name of the scenario that is ending.
     *
     * @param isPageInteractive - Indicates if this scenario is the one indicating
     * page is now interactive.
     *
     * @param endTime - Can optionally indicate when a scenario ended.
     *
     * @param properties - Set of dynamic properties that is used to track data
     *  specific to the scenario. These properties are merged with properties
     *  specified when the scenario was started. Any starting properties are
     *  replaced by their ending values.
     *
     * @param splitTimings - Serialized list of split timings, containing name and timestamp (relative to scenario start time)
     *
     * @param additionalTelemetry - Additional telemetry event data
     *
     * @returns - The scenario object used to track this scenario.
     */
    endScenario(
        featureArea: string,
        scenarioName: string,
        isPageInteractive: boolean,
        endTime?: number,
        properties?: { [key: string]: any },
        splitTimings?: ISplitTiming[],
        additionalTelemetry?: TelemetryEventData
    ): IVssScenario;

    /**
     * markTimeToInteractive is a specialized form of endScenario where the scenario
     * is generally not started, it starts when the page begins to load and is
     * complete when this is called. This method is used to specifically track when
     * the initial page is ready for the user to interact with it.
     *
     * @param featureArea - Area to associate with scenario.  Way to group scenarios which are related.
     *
     * @param scenarioName - Name of the scenario that is ending.
     *
     * @param endTime - Can optionally indicate when a scenario ended.
     *
     * @param properties - Set of dynamic properties that is used to track data
     *  specific to the scenario. These properties are merged with properties
     *  specified when the scenario was started. Any starting properties are
     *  replaced by their ending values.
     *
     * @param splitTimings - Serialized list of split timings, containing name and timestamp (relative to scenario start time)
     *
     * @param additionalTelemetry - Additional telemetry event data
     *
     * @returns - The scenario object used to track this scenario.
     */
    markTimeToInteractive(
        featureArea: string,
        scenarioName: string,
        endTime?: number,
        properties?: { [key: string]: any },
        splitTimings?: ISplitTiming[],
        additionalTelemetry?: TelemetryEventData
    ): IVssScenario;

    /**
     * startScenario is used to mark the beginning of a scenario. Initial properties
     * can be attached to the scenario and the scenario's start time is recorded
     * during this call.
     *
     * @param scenarioName - Name of the scenario that is starting.
     * @param properties - Set of dynamic properties that is used to track data specific to the scenario.
     *
     * @returns - The scenario object used to track this scenario.
     */
    startScenario(scenarioName: string, isPageInteractive: boolean, startTime?: number, properties?: { [key: string]: any }): IVssScenario;

    /**
     * Starts a scenario from the last page navigation.
     *
     * @param scenarioName - Name of the scenario that is starting.
     * @param properties - Set of dynamic properties that is used to track data specific to the scenario.
     *
     * @returns - The scenario object used to track this scenario.
     */
    startScenarioFromNavigation(scenarioName: string, isPageInteractive: boolean, properties?: { [key: string]: any }): IVssScenario;

    /**
     * Return performance events to add to each scenario from the performance API. Note: Will add measurements if not already added
     */
    getDefaultNavigationEvents(scenario?: IVssScenario): INavigationEvent[];

    /**
     * getServerStatistics will return server performance data related to the scenario load.
     */
    getServerStatistics(): IServerStatistics | null;

    /**
     * Returns any performance insights collected while processing the page.
     */
    getPerformanceInsights(): IPerformanceInsight[] | undefined;

    /**
     * Returns all scenarios which have completed.
     */
    getCompletedScenarios(): IVssScenario[];

    /**
     * Returns a scenario object if it is active, undefined otherwise
     */
    getActiveScenario(scenarioName: string): IVssScenario | undefined;

    /**
     * Return peformance entries by name
     */
    getEntriesByName(name: string): PerformanceEntry[];

    /**
     * return performance timing by name
     */
    getTimingByName(name: string): number;

    /**
     * Get telemetry details for the specified scenario
     */
    getScenarioTelemetry(scenario: IVssScenario): TelemetryEventData;

    /**
     * Whether or not an FPS event has occurred at least once on the current page.
     */
    isFastPageSwitchScenario(): boolean;

    /**
     * Adds a split timing to the specified scenario, or to all "navigate" scenarios (scenarios that start from the last page navigate/FPS)
     * if no scenario name is specified.
     *
     * @param timingName Name of the split timing to add
     * @param scenarioName Name of the scenario to add the timing to, or undefined to add to all navigation scenarios.
     */
    addSplitTiming(timingName: string, scenarioName?: string): void;

    /**
     * Get the timestamp (milliseconds) at which the last navigation event occurred (FPS or full-page navigation)
     */
    getNavigationStartTime(): number;
}

interface IVssPerformanceServiceInternal extends IVssPerformanceService {
    /**
     * Handle a scenario
     *
     * @param scenario The scenario that has been completed
     */
    handleScenarioCompleted(scenario: IVssScenario): void;
}

/**
 * Names of constanst used by performance service
 */
export const enum PerformanceConstants {
    BreakOnTtiCookie = "TFS-BREAK-TTI",
    BreakOnFirstRenderCookie = "TFS-BREAK-FIRST-RENDER",
    ReactStrictModeCookie = "TFS-REACT-STRICT",
    CollectTimingCookie = "TFS-COLLECT-TIMING",
    CollectTimingQueryParam = "collectResourceTimings"
}

/**
 * Play the lottery to see if we report resource timings
 */
const CHANCE_TO_COLLECT_RESOURCE_TIMINGS = 0.02;
const COLLECT_RESOURCE_TIMINGS = Math.random() < CHANCE_TO_COLLECT_RESOURCE_TIMINGS;

/**
 * Reason for collecting resource timing information on this scenario.
 */
const enum ResourceTimingCollectionReason {
    /**
     * Don't collect resource timings
     */
    None = 0,

    /**
     * This scenario was randomly selected to get resource timings
     */
    Random = 1,

    /**
     * This scenario was not randomly selected, but TTI was slow
     */
    SlowTTI = 2,

    /**
     * This scenario was not randomly selected, and it did not have
     * slow TTI, but a cookie or query parameter forced data to be collected.
     */
    Forced = 3
}

/**
 * Where a resource is loaded from
 */
const enum ResourceLoadSource {
    /**
     * Resource was loaded from memory cache
     */
    Memcache = 1,

    /**
     * Resource was loaded from disk cache
     */
    Diskcache = 2,

    /**
     * Resource was downloaded from a remote server
     */
    Network = 3
}

const _domainRegex = new RegExp("//(.*?)/");

/**
 * Parse a URL to find the domain portion.
 * @param url Url from which to parse the domain
 */
function domainFromUrl(url: string): string | undefined {
    if (!url) {
        return;
    }
    const reResult = _domainRegex.exec(url);
    if (reResult) {
        return reResult[1];
    }
}

class VssScenario implements IVssScenario {
    private onScenarioComplete: (scenario: IVssScenario) => void;
    public elaspsedTicks = 0;
    public endTime = -1;
    public featureArea: string = "";
    public isPageInteractive: boolean;
    public isPageNavigate: boolean;
    public properties: { [key: string]: any };
    public scenarioName: string;
    public sessionId: string;
    public splitTimings: ISplitTiming[] = [];
    public startTime: number;

    constructor(
        scenarioName: string,
        sessionId: string,
        isPageInteractive: boolean,
        isPageNavigate: boolean,
        startTime: number,
        properties: { [key: string]: any } | undefined,
        onScenarioComplete: (scenario: IVssScenario) => void
    ) {
        this.scenarioName = scenarioName;
        this.isPageInteractive = isPageInteractive;
        this.isPageNavigate = isPageNavigate;
        this.startTime = startTime;
        this.properties = { ...properties };
        this.sessionId = sessionId;
        this.onScenarioComplete = onScenarioComplete;

        if (!isPageNavigate) {
            Timing.mark(markNameForScenario(scenarioName, MarkNameMode.Start));
        }
    }

    public complete(endTime?: number): void {
        if (this.isPageNavigate && !endTime && !document.hidden) {
            // For page navigate events, wait until next paint before calling the scenario complete
            // Note: don't do this for hidden tabs which won't invoke the callback until the tab
            // becomes visible
            this.splitTimings.push({ name: "ScenarioEnd.PendingPaint", timestamp: Date.now() - this.startTime });
            window.requestAnimationFrame(() => {
                this.completeNow();
            });
        } else {
            this.completeNow(endTime);
        }
    }

    public addProperties(properties: { [key: string]: any }): void {
        this.properties = { ...this.properties, ...properties };
    }

    private completeNow(endTime?: number): void {
        this.endTime = endTime || Date.now();
        this.elaspsedTicks = this.endTime - this.startTime;
        this.onScenarioComplete(this);

        if (!endTime || this.isPageNavigate) {
            const markEndName: string = markNameForScenario(this.scenarioName, MarkNameMode.End);
            Timing.mark(markEndName);

            // Add scenario to timeline if no explicit end time is given, or if it's a page navigate scenario
            const markStartName: string = this.isPageNavigate ? "navigationStart" : markNameForScenario(this.scenarioName, MarkNameMode.Start);
            Timing.measure(this.scenarioName, markStartName, markEndName);
        }
    }
}

class VssPerformanceService extends VssObservableService<IVssScenario, "scenarioComplete"> implements IVssPerformanceService {
    private performanceData: IVssPerformanceData;

    private activeScenarios: { [scenarioName: string]: IVssScenario } = {};
    private completedScenarios: IVssScenario[] = [];
    private isDebugMode: boolean;
    private navigationStartTime: number;
    private pageId: string;
    private pageNavigateScenario: boolean = true;
    private pageNavigateSplitTimings: ISplitTiming[] = [];
    private serviceVersion: string;
    private sessionId: string;

    // These are the statistics for the most recent page switch.
    private serverStatistics: IServerStatistics | null;
    private performanceInsights: IPerformanceInsight[] | undefined;

    private _completedContentCache: ICompletedContent[] = [];

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        this.resetPerformanceData(true);

        this.navigationStartTime = window.performance.timing["navigationStart"];

        // Register with the content service to be notified about all the content being loaded.
        const contentService = this.pageContext.getService<IVssContentService>("IVssContentService");
        contentService.subscribe(this._contentComplete, "contentComplete");
        contentService.subscribe(this._contentComplete, "contentLoadError");

        // Track the set of completed fetch requests for performance measurements.
        requestListeners.subscribe(this._afterRequest, "afterRequest");

        document.addEventListener("scenarioStarted", this.legacyScenarioStarted);
        document.addEventListener("scenarioCompleted", this.legacyScenarioCompleted);
        document.addEventListener("legacyContentElementLoaded", this._legacyContentComplete);

        // Now that the page is loaded we will initialize the service. This is required to allow all scripts to process.
        this.initializeService();

        // We want to know when the page begins processing an FPS event.
        document.body.addEventListener("fpsLoading", this.fpsLoading);
    }

    public _serviceRestart(pageContext: IVssPageContext) {
        super._serviceRestart(pageContext);

        this.resetPerformanceData(false);
        this.initializeService();
    }

    public isFastPageSwitchScenario(): boolean {
        return !this.pageNavigateScenario;
    }

    public addSplitTiming(timingName: string, scenarioName?: string): void {
        if (scenarioName) {
            const scenario = this.activeScenarios[scenarioName];
            if (scenario) {
                scenario.splitTimings.push({
                    name: timingName,
                    timestamp: Date.now() - scenario.startTime
                });
            }
        } else {
            // Add the split timing to "navigate" scenarios
            this.pageNavigateSplitTimings.push({
                name: timingName,
                timestamp: Date.now() - this.navigationStartTime
            });
        }
    }

    public getCompletedScenarios(): IVssScenario[] {
        return this.completedScenarios;
    }

    public getActiveScenario(scenarioName: string): IVssScenario | undefined {
        return this.activeScenarios[scenarioName];
    }

    public getServerStatistics(): IServerStatistics | null {
        return this.serverStatistics;
    }

    public getPerformanceInsights(): IPerformanceInsight[] | undefined {
        return this.performanceInsights;
    }

    public getEntriesByName(name: string): PerformanceEntry[] {
        return Timing.getEntriesByName(name);
    }

    public getTimingByName(name: string): number {
        return Timing.getTimingByName(name);
    }

    public getPerformanceData(): IVssPerformanceData {
        return this.performanceData;
    }

    public endScenario(
        featureArea: string,
        scenarioName: string,
        isPageInteractive: boolean,
        endTime?: number,
        properties?: { [key: string]: any },
        splitTimings?: ISplitTiming[],
        additionalTelemetry?: TelemetryEventData
    ): IVssScenario {
        let completedScenario = this.activeScenarios[scenarioName];

        if (!completedScenario) {
            completedScenario = this.startScenarioFromNavigation(scenarioName, isPageInteractive, properties);
        } else {
            // Merge-in new properties on the existing scenario
            if (isPageInteractive) {
                completedScenario.isPageInteractive = true;
            }
            if (properties) {
                completedScenario.addProperties(properties);
            }
        }

        if (featureArea) {
            completedScenario.featureArea = featureArea;
        }

        if (splitTimings) {
            completedScenario.splitTimings.push(...splitTimings);
        }

        if (additionalTelemetry) {
            if (additionalTelemetry.properties) {
                completedScenario.addProperties(additionalTelemetry.properties);
            }
            if (additionalTelemetry.serviceInstanceType) {
                completedScenario.properties["serviceInstanceType"] = additionalTelemetry.serviceInstanceType;
            }
        }

        completedScenario.complete(endTime);

        return completedScenario;
    }

    public markTimeToInteractive(
        featureArea: string,
        scenarioName: string,
        endTime?: number,
        properties?: { [key: string]: any },
        splitTimings?: ISplitTiming[],
        additionalTelemetry?: TelemetryEventData
    ): IVssScenario {
        return this.endScenario(featureArea, scenarioName, true, endTime, properties, splitTimings, additionalTelemetry);
    }

    public startScenario(scenarioName: string, isPageInteractive: boolean, startTime?: number, properties?: { [key: string]: any }): IVssScenario {
        return this._startScenario(scenarioName, isPageInteractive, false, startTime || Date.now(), properties);
    }

    public startScenarioFromNavigation(scenarioName: string, isPageInteractive: boolean, properties?: { [key: string]: any }): IVssScenario {
        return this._startScenario(scenarioName, isPageInteractive, this.pageNavigateScenario, this.navigationStartTime, properties);
    }

    public getDefaultNavigationEvents(scenario?: IVssScenario): INavigationEvent[] {
        const events: INavigationEvent[] = [];

        try {
            const expectedDefaultEvents: INavigationEvent[] = [
                { name: "PLT", startEvent: "navigationStart", endEvent: "loadEventEnd", isAggregate: true },
                { name: "Redirect", startEvent: "redirectStart", endEvent: "redirectEnd" },
                { name: "Unload", startEvent: "unloadEventStart", endEvent: "unloadEventEnd" },
                { name: "Fetch", startEvent: "fetchStart", endEvent: "domainLookupStart" },
                { name: "DNS", startEvent: "domainLookupStart", endEvent: "domainLookupEnd" },
                { name: "TCP", startEvent: "connectStart", endEvent: "connectEnd" },
                { name: "SSL", startEvent: "secureConnectionStart", endEvent: "connectEnd" },
                // Use connectEnd instead of requestStart for request/server time timings, to work around IE11 issue with requestStart
                { name: "Request", startEvent: "connectEnd", endEvent: "responseStart" },
                { name: "Server Time", startEvent: "connectEnd", endEvent: "responseEnd", isAggregate: true },
                { name: "Response", startEvent: "responseStart", endEvent: "responseEnd" },
                { name: "DOMInteractive", startEvent: "domInteractive", endEvent: "domComplete" },
                { name: "DOMProcessing", startEvent: "domLoading", endEvent: "domComplete" },
                { name: "onLoad", startEvent: "loadEventStart", endEvent: "loadEventEnd" },
                { name: "Network Time", startEvent: "domainLookupStart", endEvent: "connectEnd" },
                { name: "PreRequest", startEvent: "navigationStart", endEvent: "connectEnd", isAggregate: true },
                { name: "RequestResponse", startEvent: "connectEnd", endEvent: "responseEnd", isAggregate: true }
            ];

            for (const expectedDefaultEvent of expectedDefaultEvents) {
                let entries: PerformanceEntry[] = Timing.getEntriesByName(expectedDefaultEvent.name);
                if (entries.length === 0) {
                    // The event has not been measured yet. Make sure both the start and end events have been triggered.
                    // For some marks, this is not a timing, it's an entry, so check for presence in both
                    if (
                        (Timing.getTimingByName(expectedDefaultEvent.startEvent) == null &&
                            Timing.getEntriesByName(expectedDefaultEvent.startEvent).length === 0) ||
                        (Timing.getTimingByName(expectedDefaultEvent.endEvent) == null &&
                            Timing.getEntriesByName(expectedDefaultEvent.endEvent).length === 0)
                    ) {
                        // At least one of the requested timings has not been recorded yet, skip this event.
                        continue;
                    }

                    Timing.measure(expectedDefaultEvent.name, expectedDefaultEvent.startEvent, expectedDefaultEvent.endEvent);
                    entries = Timing.getEntriesByName(expectedDefaultEvent.name);
                }

                // Create deep copies of object to work around Chrome bug where PerformanceEntry stopped having a serializer
                // (serializer is not required by W3C spec).
                const newEntry = entries.map(
                    ev =>
                        ({
                            name: ev.name,
                            startTime: ev.startTime,
                            duration: ev.duration
                        } as IMappedPerformanceEntry)
                )[0];
                expectedDefaultEvent.perfEntry = newEntry;
                events.push(expectedDefaultEvent);
            }
        } catch (error) {
            if ($DEBUG) {
                console.error("Unexpected error: " + error);
            }
        }

        if (scenario) {
            let duration = 0;
            if (scenario.endTime) {
                duration = scenario.startTime - scenario.endTime;
            }

            const pageInteractiveEvent: INavigationEvent = {
                name: "TTI",
                perfEntry: {
                    name: "TTI",
                    duration: duration,
                    startTime: 0
                },
                startEvent: "navigationStart",
                endEvent: scenario.scenarioName,
                isAggregate: true
            };

            if (pageInteractiveEvent) {
                events.push(pageInteractiveEvent);
            }
        }

        return events;
    }

    /**
     * Get the performance timing entries with the specified name
     *
     * @param name The name of the timing entries to get
     */
    public getResourceTimingEntries(): PerformanceResourceTiming[] {
        return (Timing.getEntriesByType("resource") as PerformanceResourceTiming[]) || [];
    }

    public getTimingTelemetry(reason: ResourceTimingCollectionReason): TelemetryEventData | null {
        const contentTelemetryProperties = this._getContentTelemetryProperties(reason);

        if (!contentTelemetryProperties) {
            return null;
        }
        const telemetryPayload = {
            area: "Performance",
            feature: "ResourceTiming",
            properties: contentTelemetryProperties
        };

        if (telemetryPayload.properties.individualTimings) {
            // Check the size of the payload and cull some entries if necessary
            let payloadStr = JSON.stringify(telemetryPayload, null, 2);

            // 64 kB max size = 32k length, but account for ETW headers
            const maxPayloadLength = 30000;
            const approximateCharactersPerTimingEntry = 450;
            while (payloadStr.length > maxPayloadLength) {
                const lengthOverweight = payloadStr.length - maxPayloadLength;
                const numberOfEntriesToRemove = Math.ceil(lengthOverweight / approximateCharactersPerTimingEntry);
                const entriesKeys = Object.keys(telemetryPayload.properties.individualTimings);

                // Going for at least 10 resource timings in this event.
                if (entriesKeys.length <= 10) {
                    return null;
                }
                const keysToDelete = entriesKeys.slice(Math.max(20, entriesKeys.length - numberOfEntriesToRemove));

                for (const key of keysToDelete) {
                    delete telemetryPayload.properties.individualTimings[key];
                }
                payloadStr = JSON.stringify(telemetryPayload, null, 2);
            }
        }

        return telemetryPayload;
    }

    public getScenarioTelemetry(scenario: IVssScenario, serviceInstanceType?: string): TelemetryEventData {
        let properties: IScenarioTelemetryData = {
            activityId: scenario.sessionId,
            backgroundTab: document.hidden,
            debugMode: this.isDebugMode,
            elapsedTime: scenario.endTime - scenario.startTime,
            featureArea: scenario.featureArea || "",
            name: scenario.scenarioName,
            pageId: this.pageId,
            pageNavigateScenario: scenario.isPageNavigate,
            serviceVersion: this.serviceVersion,
            splitTimings: scenario.splitTimings,
            startTime: scenario.startTime,
            timeZoneOffset: new Date().getTimezoneOffset()
        };

        properties = { ...scenario.properties, ...properties };

        if (scenario.isPageInteractive) {
            try {
                // Best-effort attempt to add page interactive properties. Don't throw attempting to
                // report telemetry.
                const interactiveProperties = this._getPageInteractiveTelemetryProperties(properties.pageNavigateScenario);
                Object.assign(properties, interactiveProperties);
            } catch {}
        }

        return {
            area: "Performance",
            feature: "Scenario",
            elapsedTime: scenario.endTime - scenario.startTime,
            properties: properties,
            serviceInstanceType
        };
    }

    public getNavigationStartTime(): number {
        return this.navigationStartTime;
    }

    private _startScenario(
        scenarioName: string,
        isPageInteractive: boolean,
        isPageNavigate: boolean,
        startTime: number,
        properties?: { [key: string]: any }
    ): IVssScenario {
        const activeScenario = new VssScenario(
            scenarioName,
            this.sessionId,
            isPageInteractive,
            isPageNavigate,
            startTime,
            properties,
            this.handleScenarioComplete
        );
        this.activeScenarios[scenarioName] = activeScenario;
        return activeScenario;
    }

    private handleScenarioComplete = (scenario: IVssScenario): void => {
        if (scenario === this.activeScenarios[scenario.scenarioName]) {
            delete this.activeScenarios[scenario.scenarioName];
        }

        this.completedScenarios.push(scenario);

        if ((scenario.isPageNavigate || scenario.isPageInteractive) && this.pageNavigateSplitTimings.length) {
            scenario.splitTimings.push(...this.pageNavigateSplitTimings);
            scenario.splitTimings.sort((a, b) => a.timestamp - b.timestamp);
        }

        if (scenario.isPageInteractive) {
            // Record the overall scenarioPerformanceData tti value.
            this.performanceData.timeToInteractive = scenario.endTime - scenario.startTime;
            this.performanceData.backgroundTab = document.hidden;

            // Set a new session id so that any scenarios started after TTI are not
            // associated with this activity ID.
            this.setNewSessionId();
        }

        // Publish telemetry
        const telemetryData = this.getScenarioTelemetry(scenario, scenario.properties["serviceInstanceType"]);
        telemetryData.properties["LWLS"] = true;
        if (scenario.isPageInteractive) {
            telemetryData.properties["isTTI"] = true;
            telemetryData.properties["theme"] = getCurrentTheme(this.pageContext).id;
        }
        publishEvent(this.pageContext, telemetryData.area, telemetryData.feature, telemetryData.properties, telemetryData.serviceInstanceType);

        // report timing telemetry for individual resources.
        if (scenario.isPageInteractive) {
            const reason = this._shouldCollectResourceTimings();
            const resourceTelemetry = this.getTimingTelemetry(reason);
            if (resourceTelemetry) {
                publishEvent(
                    this.pageContext,
                    resourceTelemetry.area,
                    resourceTelemetry.feature,
                    resourceTelemetry.properties,
                    telemetryData.serviceInstanceType
                );
            }

            // Clear our cached resources so that any subsequent TTI events won't have timing entries
            // polluted by data from previous events.
            this._completedContentCache = [];
        }

        // Notify any listeners about the completion of the scenario.
        this._notify(scenario, "scenarioComplete");

        if (scenario.isPageInteractive) {
            const contributedEventService = this.pageContext.getService<IVssContributionEventService>("IVssContributionEventService");
            setTimeout(() => {
                // Break on TTI when the cookie is set
                const cookieValue = getCookie(PerformanceConstants.BreakOnTtiCookie);
                if (cookieValue === "enabled") {
                    debugger;
                }

                // Fire the page-interactive event on next-tick so that we ensure the browser has performed a paint
                // before we start kicking off page-interactive event handlers. Since we are triggered here on
                // requestAnimationFrame, anything that happens synchronously here will block the browser from painting
                contributedEventService.fireEvent("page-interactive", scenario, true);
            }, 0);
        }
    };

    private initializeService() {
        const pageDataService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageDataService.getData();

        if (pageData) {
            this.isDebugMode = pageData.isDebugMode;
            this.sessionId = pageData.sessionId;
            this.pageId = pageData.sessionId;
            this.serviceVersion = pageData.serviceVersion;
        }

        // Fill in the serverPerformanceData from the data sent down in the request.
        const statsData = readJsonScript<IServerStatisticsData>("serverStatistics", true);
        this.serverStatistics = statsData ? statsData.serverTimings : null;
        this.performanceInsights = statsData ? statsData.insights : undefined;
        if (this.serverStatistics) {
            for (const statisticName in this.serverStatistics) {
                const statistic = this.serverStatistics[statisticName];
                switch (statisticName) {
                    case "AAD":
                    case "ServiceBus":
                        this.performanceData.serverPerformanceData.totalRemote += statistic.count;
                        break;

                    case "BlobStorage":
                    case "TableStorage":
                        this.performanceData.serverPerformanceData.storage += statistic.count;
                        this.performanceData.serverPerformanceData.totalRemote += statistic.count;
                        break;

                    case "Redis":
                        this.performanceData.serverPerformanceData.redis += statistic.count;
                        this.performanceData.serverPerformanceData.totalRemote += statistic.count;
                        break;

                    case "SQL":
                        this.performanceData.serverPerformanceData.sql += statistic.count;
                        this.performanceData.serverPerformanceData.totalRemote += statistic.count;
                        break;

                    case "VssClient":
                        this.performanceData.serverPerformanceData.rest += statistic.count;
                        this.performanceData.serverPerformanceData.totalRemote += statistic.count;
                        break;
                }
            }
        }
    }

    /**
     * Generates a random session ID for the next scenario
     */
    private setNewSessionId(): void {
        this.sessionId = newGuid();
    }

    private _getPageInteractiveTelemetryProperties(pageNavigateScenario: boolean): { [key: string]: any } {
        const perfEntries: { [key: string]: { start: number; duration: number } } = {};
        const properties = {
            resourceCount: this.performanceData.contentData.cssCount + this.performanceData.contentData.scriptsCount,
            scriptResourceCount: this.performanceData.contentData.scriptsCount,
            cssResourceCount: this.performanceData.contentData.cssCount,
            ajaxRequests: this.performanceData.contentData.ajaxCount,
            scriptSize: this.performanceData.contentData.scriptSize,
            cssSize: this.performanceData.contentData.cssSize
        } as { [key: string]: any };

        if (pageNavigateScenario) {
            const navigationEvents: INavigationEvent[] = this.getDefaultNavigationEvents();
            for (let i = 0; i < navigationEvents.length; i++) {
                const perfEntry = navigationEvents[i].perfEntry;
                if (perfEntry) {
                    perfEntries[perfEntry.name] = {
                        start: perfEntry.startTime,
                        duration: perfEntry.duration
                    };
                }
            }

            properties["navigation"] = perfEntries;

            const resourceLoadStrategy = window.resourceLoadStrategy;
            if (resourceLoadStrategy) {
                properties["resourceLoadStrategy"] = resourceLoadStrategy;
            }
        }

        // best effort attempt to add window size and connection properties
        try {
            properties["screen"] = {
                h: window.screen.height,
                w: window.screen.width,
                winH: window.innerHeight,
                winW: window.innerWidth,
                winOH: window.outerHeight,
                winOW: window.outerWidth
            };

            const navigator: any = (window as any).navigator;
            if (navigator && navigator.connection) {
                properties["connection"] = {
                    rtt: navigator.connection.rtt,
                    type: navigator.connection.effectiveType,
                    downlink: navigator.connection.downlink
                };
            }
        } catch {}

        return properties;
    }

    private resetPerformanceData(initialPageLoad: boolean) {
        this.performanceData = {
            timeToInteractive: 0,
            backgroundTab: false,
            contentData: { ajaxCount: 0, ajaxSize: 0, cssCount: 0, cssSize: 0, other: "", scriptsCount: 0, scriptSize: 0 },
            serverPerformanceData: { redis: 0, rest: 0, sql: 0, storage: 0, totalRemote: 0 },
            initialPageLoad: initialPageLoad
        };
    }

    private _afterRequest = (postRequestEvent: IPostRequestEvent, action: RequestListenerAction): void => {
        this.performanceData.contentData.ajaxCount++;
        if (postRequestEvent.response && postRequestEvent.response.ok) {
            const contentLength = postRequestEvent.response.headers.get("content-length");
            if (contentLength) {
                this.performanceData.contentData.ajaxSize += parseInt(contentLength);
            }
        }
    };

    private _contentComplete = (completedContent: ICompletedContent, action: "contentComplete" | "contentLoadError"): void => {
        // Track the aggregation of data throughout the length of the page.
        if (completedContent.contentAttributes.contentType === "text/css") {
            this.performanceData.contentData.cssCount++;
            this.performanceData.contentData.cssSize += completedContent.contentAttributes.contentLength;
        } else if (completedContent.contentAttributes.contentType === "text/javascript") {
            this.performanceData.contentData.scriptsCount++;
            this.performanceData.contentData.scriptSize += completedContent.contentAttributes.contentLength;
        }

        // if we've already marked tti, we've already reported the data for this page load, so no need to collect more
        if (action === "contentComplete" && !this.performanceData.timeToInteractive) {
            this._completedContentCache.push(completedContent);
        }
    };

    private _legacyContentComplete = (e: CustomEvent<{ isCss: boolean; size: number }>): void => {
        if (e && e.detail && e.detail.size) {
            if (e.detail.isCss) {
                this.performanceData.contentData.cssCount++;
                this.performanceData.contentData.cssSize += e.detail.size;
            } else {
                this.performanceData.contentData.scriptsCount++;
                this.performanceData.contentData.scriptSize += e.detail.size;
            }
        }
    };

    /**
     * Determines if resource timings should be collected and reported. Data is only important when
     * debug mode is disabled, bundling is on, and CDN is used. To reduce the amount of data we log,
     *
     */
    private _shouldCollectResourceTimings(): ResourceTimingCollectionReason {
        const pageDataService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageDataService.getData();
        const forcedCollection =
            window.location.href.indexOf(`${PerformanceConstants.CollectTimingQueryParam}=true`) >= 0 ||
            document.cookie.indexOf(`${PerformanceConstants.CollectTimingCookie}=true`) >= 0;
        const slowLoad = this.performanceData.timeToInteractive > 8000;
        if (pageData) {
            const stdRequirements = pageData.isBundlingEnabled && !pageData.isDebugMode && pageData.isCdnEnabled;
            if (stdRequirements) {
                if (COLLECT_RESOURCE_TIMINGS) {
                    return ResourceTimingCollectionReason.Random;
                }
                if (slowLoad) {
                    return ResourceTimingCollectionReason.SlowTTI;
                }
            }
            if (forcedCollection) {
                return ResourceTimingCollectionReason.Forced;
            }
        }
        return ResourceTimingCollectionReason.None;
    }

    /**
     * Index resource timings by URL
     */
    private _getContentTelemetryProperties(reason: ResourceTimingCollectionReason): ContentTelementryProperties | null {
        const aggregate: AggregateResourceTelemetry = {
            cdnResourceCount: 0,
            diskcachedResourceCount: 0,
            memcachedResourceCount: 0,
            resourceCount: 0,
            skippedResources: 0,
            totalDuration: 0,
            totalTransferSize: 0
        };
        const contentTelemetryProperties: ContentTelementryProperties = { aggregate, reason };
        if (reason !== ResourceTimingCollectionReason.None) {
            contentTelemetryProperties.individualTimings = {};
        }
        if (this._completedContentCache.length === 0) {
            return null;
        }
        try {
            const resourceTimingIndex: { [resourceId: string]: PerformanceResourceTiming | undefined } = {};
            const entries = this.getResourceTimingEntries();
            for (const timingEntry of entries) {
                resourceTimingIndex[timingEntry.name] = timingEntry;
            }

            for (const completedContent of this._completedContentCache) {
                if (!completedContent.contentAttributes.clientId) {
                    continue;
                }
                const url =
                    completedContent.contentElement instanceof HTMLLinkElement
                        ? completedContent.contentElement.href
                        : completedContent.contentElement.src;
                const entry = resourceTimingIndex[url];
                const domain = domainFromUrl(url);
                let source, totalDuration, transferSize;
                if (entry) {
                    transferSize = (entry as any).transferSize;
                    totalDuration = Math.round(entry.duration);
                    source = this._extrapolateResourceLoadSource(entry);
                    if (domain) {
                        aggregate.resourceCount++;
                        aggregate.totalTransferSize += transferSize;
                        aggregate.cdnResourceCount += domain.indexOf("cdn") >= 0 ? 1 : 0;
                        aggregate.diskcachedResourceCount += source === ResourceLoadSource.Diskcache ? 1 : 0;
                        aggregate.memcachedResourceCount += source === ResourceLoadSource.Memcache ? 1 : 0;
                        aggregate.totalDuration += totalDuration;

                        // Full navigations set previousResourceLoadEnd but not loadStart.  FPS navigations do the opposite.
                        if (completedContent.previousResourceLoadEnd) {
                            aggregate.resourceStartTime = Math.min(
                                aggregate.resourceStartTime || completedContent.previousResourceLoadEnd,
                                completedContent.previousResourceLoadEnd
                            );
                        } else if (completedContent.loadStart) {
                            aggregate.resourceStartTime = Math.min(
                                aggregate.resourceStartTime || completedContent.loadStart,
                                completedContent.loadStart
                            );
                        }

                        if (completedContent.loadEnd) {
                            aggregate.resourceEndTime = Math.max(aggregate.resourceEndTime || completedContent.loadEnd, completedContent.loadEnd);
                        }
                    }
                }
                if (contentTelemetryProperties.individualTimings) {
                    const attributes = {
                        contentType: completedContent.contentAttributes.contentType,
                        contributionId: completedContent.contentAttributes.contributionId,
                        domain,
                        loadStart: completedContent.loadStart,
                        loadEnd: completedContent.loadEnd,
                        previousResourceLoadEnd: completedContent.previousResourceLoadEnd
                    } as IndividualResourceTelemetry;

                    // If possible, augment the attributes with data we get from the browser's performance metrics.
                    if (entry) {
                        Object.assign(attributes, {
                            // some props aren't in the .d.ts (and may be undefined), but that's okay.
                            decodedBodySize: (entry as any).decodedBodySize,
                            encodedBodySize: (entry as any).encodedBodySize,
                            source,
                            totalDuration,
                            transferSize
                        });
                        if (entry.requestStart) {
                            attributes.requestStart = entry.requestStart;
                        }
                        if (entry.responseStart) {
                            attributes.responseStart = entry.responseStart;
                        }
                        if (entry.responseEnd) {
                            attributes.responseEnd = entry.responseEnd;
                        }
                    }

                    contentTelemetryProperties.individualTimings[completedContent.contentAttributes.clientId] = attributes;
                }
            }
        } catch (e) {
            // don't throw trying to collect telemetry data
            console.warn(("Error while collecting telemetry data: " + e && e.message) || e.toString());
        }
        return contentTelemetryProperties;
    }

    /**
     * Do a "best guess" to determine whether a resource was loaded from memory
     * cache, disk cache, or the network. Resources in memory cache have practically
     * no latency between request start and response end, and disk cache has extremely
     * low latency between request start and response start. Anything else we assume
     * has come from the network.
     */
    private _extrapolateResourceLoadSource(entry: PerformanceResourceTiming): ResourceLoadSource {
        const requestStartToResponseStart = entry.responseStart - entry.requestStart;
        const requestStartToResponseEnd = entry.responseEnd - entry.requestStart;

        if (requestStartToResponseEnd < 10) {
            return ResourceLoadSource.Memcache;
        } else if (requestStartToResponseStart < 60) {
            return ResourceLoadSource.Diskcache;
        } else {
            return ResourceLoadSource.Network;
        }
    }

    private fpsLoading = (event: FastPageSwitchEvent<FastPageSwitchEventLoadingDetail>) => {
        // Set the navigation start time
        this.pageNavigateScenario = false;
        this.navigationStartTime = event.detail.startTime;

        // Set a window variable to mark the current navigation time for use by
        // the legacy web platform which may not have loaded yet.
        (window as any).lwpLastNavigationStartTime = this.navigationStartTime;
        document.body.dispatchEvent(new CustomEvent("lwpPerfNavigationReset", { cancelable: false, detail: {} }));

        // Clear all ongoing and completed scenarios
        this.activeScenarios = {};
        this.completedScenarios = [];

        // Clear navigation split timings
        this.pageNavigateSplitTimings = [];
    };

    private legacyScenarioStarted = (scenarioEvent: any) => {
        const scenario = scenarioEvent.detail.descriptor;
        if (scenario) {
            this._startScenario(scenario.getName(), scenario.isPageInteractive(), this.pageNavigateScenario, scenario.getStartTime());
        }
    };

    private legacyScenarioCompleted = (scenarioEvent: any) => {
        const scenarioDetail: any = scenarioEvent.detail.scenarioDetail;

        if (scenarioDetail) {
            const scenarioDescriptor: any = scenarioDetail.scenario;
            const scenarioTelemtry: TelemetryEventData = scenarioDetail.telemetryData;

            if (scenarioDescriptor.isPageInteractive()) {
                this.markTimeToInteractive(
                    scenarioDescriptor.getFeatureArea(),
                    scenarioDescriptor.getName(),
                    undefined,
                    scenarioDescriptor.getData(),
                    scenarioDescriptor.getSplitTimings(),
                    scenarioTelemtry
                );
            } else {
                this.endScenario(
                    scenarioDescriptor.getFeatureArea(),
                    scenarioDescriptor.getName(),
                    false,
                    scenarioDescriptor.getEndTime(),
                    scenarioDescriptor.getData(),
                    scenarioDescriptor.getSplitTimings(),
                    scenarioTelemtry
                );
            }
        }
    };
}

Services.add("IVssPerformanceService", { serviceFactory: VssPerformanceService, options: ServiceOptions.Persistant | ServiceOptions.Startup });

export namespace Timing {
    export function mark(markName: string): void {
        window.performance && window.performance.mark && window.performance.mark(markName);
        console && (console as any).timeStamp && (console as any).timeStamp(markName);
    }

    /** Determine whether a mark can be safely passed to `measure` */
    function canMeasureMark(markName: string): boolean {
        return navigationTimingMarkHit(markName) || getEntriesByName(markName).length > 0;
    }

    export function measure(measureName: string, startMarkName: string, endMarkName: string): void {
        if (window.performance && window.performance.measure) {
            if (!canMeasureMark(startMarkName) || !canMeasureMark(endMarkName)) {
                // Both marks need to exist, otherwise we cannot create a measure
                return;
            }

            window.performance.measure(measureName, startMarkName, endMarkName);
        }
    }

    /** Determine whether the given mark name is a navigation event and if it is, if it has occurred yet */
    function navigationTimingMarkHit(markName: string): boolean {
        const mark = (window.performance && window.performance.timing && (window.performance.timing as any)[markName]) || null;
        return typeof mark !== "undefined" && mark !== null;
    }

    export function getEntriesByType(entryType: string): PerformanceEntry[] {
        return (window.performance && window.performance.getEntriesByType && window.performance.getEntriesByType(entryType)) || [];
    }

    export function getEntriesByName(entryName: string): PerformanceEntry[] {
        return (window.performance && window.performance.getEntriesByName && window.performance.getEntriesByName(entryName)) || [];
    }

    export function getMeasures(name?: string): PerformanceEntry[] {
        return getEntriesByType("measure");
    }

    export function getMarks(): PerformanceEntry[] {
        return getEntriesByType("mark");
    }

    export function getTimingByName(name: string): number {
        return (window.performance && window.performance.timing && (window.performance as any).timing[name]) || null;
    }
}

enum MarkNameMode {
    Start,
    End
}

/**
 * Gets a mark name for a scenario
 * @param scenarioName Scenario name to get mark name for
 * @param mode Start/end mode for mark name
 */
function markNameForScenario(scenarioName: string, mode: MarkNameMode): string {
    return `${scenarioName}-${mode === MarkNameMode.Start ? "start" : "end"}`;
}
